import {
  InteractionStatus,
  PublicClientApplication,
} from "@azure/msal-browser";
import {
  AuthenticatedTemplate,
  MsalProvider,
  useMsal,
} from "@azure/msal-react";
import React, { useEffect, useState } from "react";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { initializeAuth, msalConfig } from "./authConfig";
import AuthBackground from "./components/auth/AuthBackground";
import Login from "./components/auth/Login";
import Logout from "./components/auth/Logout";
import { User } from "./components/auth/User";
import { CardSelectorProvider } from "./components/common/CardSelectorProvider";
import { ConfirmationProvider } from "./components/common/ConfirmationContext";
import ConfirmationDialog from "./components/common/ConfirmationDialog";
import { LoadingProvider } from "./components/common/LoadingContext";
import LoadingControl from "./components/common/LoadingControl";
import NotImplemented from "./components/common/NotImplemented";
import { PinsProvider } from "./components/common/PinsProvider";
import { ToastProvider } from "./components/common/ToastContext";
import CustomPronunciationConsole from "./components/custom_pronunciation/CustomPronunciationConsole";
import DocumentationDesktop from "./components/doc/DocumentationDesktop";
import AboutModal from "./components/help/AboutModal";
import HelpModal from "./components/help/HelpModal";
import {
  HelpModalProvider,
  useHelpModal,
} from "./components/help/HelpModalContext";
import Background from "./components/layout/Background";
import { MenuProvider } from "./components/layout/MenuContext";
import Navbar from "./components/layout/Navbar";
import { updateRecents } from "./components/layout/recentUtils";
import CoreModelDesktop from "./components/models/CoreModelDesktop";
import FineTunedModelDesktop from "./components/models/FineTunedModelDesktop";
import TrainingDataDesktop from "./components/models/TrainingDataDesktop";
import PersonaDesktop from "./components/persona/PersonaDesktop";
import UserProfile from "./components/profile/UserProfile";
import SessionConsole from "./components/sessions/SessionConsole";
import FragmentsConsole from "./components/settings/FragmentsDesktop";
import SettingsConsole from "./components/settings/SettingsDesktop";
import ComplianceOnboarding from "./components/onboarding/ComplianceOnboarding";
import SkillDesktop from "./components/skills/SkillDesktop";
import TopicDesktop from "./components/topics/TopicDesktop";
import OnDemandConsole from "./components/validation/ondemand/OnDemandConsole";
import PresentationDesktop from "./components/validation/presentations/PresentationDesktop";
import PresentationDisplay from "./components/validation/presentations/PresentationDisplay";
import { saveRedirectUrl } from "./utils/redirectUtils";
import ActivityTracker from "./components/common/ActivityTracker";
import UserAnalysisDashboard from "./components/analysis/users/UserMonitor";
import SimulantDesktop from "./components/simulants/SimulantDesktop";
import BrowserWarning, { isChrome } from "./components/layout/BrowserWarning";
import { usePollForBuildId } from "./hooks/usePollForBuildId";
import AssignmentsDashboard from "./components/assignments/AssignmentsDashboard";
import DashboardHome from "./components/dashboard/DashboardHome";
import OutreachMonitor from "./components/outreach/OutreachMonitor";
import TwilioSenderDesktop from "./components/twilio_sender/TwilioSenderDesktop";

// Initialize auth config first
await initializeAuth();

export const msalInstance = new PublicClientApplication(msalConfig);
await msalInstance.initialize();

function UnprotectedRoute({ children }) {
  return <AuthBackground>{children}</AuthBackground>;
}

function ProtectedRoute({ children, openAboutModal }) {
  const { inProgress, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (inProgress === InteractionStatus.None && accounts.length === 0) {
      saveRedirectUrl(location.pathname + location.search);
      navigate("/login");
    } else {
      updateRecents(location.pathname);
    }
  }, [inProgress, accounts, location, navigate]);

  if (inProgress !== InteractionStatus.None || accounts.length === 0) {
    return null; // or a loading indicator
  }

  return (
    <AuthenticatedTemplate>
      <MenuProvider>
        <Background>
          <Navbar openAboutModal={openAboutModal} />
          {children}
        </Background>
      </MenuProvider>
    </AuthenticatedTemplate>
  );
}

function AuthOnlyRoute({ children }) {
  const { inProgress, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (inProgress === InteractionStatus.None && accounts.length === 0) {
      saveRedirectUrl(location.pathname + location.search);
      navigate("/login");
    }
  }, [inProgress, accounts, location, navigate]);

  if (inProgress !== InteractionStatus.None || accounts.length === 0) {
    return null; // or a loading indicator
  }

  return <AuthenticatedTemplate>{children}</AuthenticatedTemplate>;
}

function AppRoutes() {
  const { isOpen, fileName, closeHelpModal } = useHelpModal();
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);

  const openAboutModal = () => setIsAboutModalOpen(true);
  const closeAboutModal = () => setIsAboutModalOpen(false);

  return (
    <MsalProvider instance={msalInstance}>
      <ActivityTracker />
      <Routes>
        {/* Unprotected Routes */}
        <Route
          path="/login"
          element={
            <UnprotectedRoute>
              <Login />
            </UnprotectedRoute>
          }
        />
        <Route
          path="/logout"
          element={
            <UnprotectedRoute>
              <Logout />
            </UnprotectedRoute>
          }
        />
        {/* Protected Routes */}
        {/* <Route
          path="/"
          element={
            <ProtectedRoute
              
              openAboutModal={openAboutModal}
            >
              <ConceptMap />
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <DashboardHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assignments"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <AssignmentsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/doc"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <DocumentationDesktop />
            </ProtectedRoute>
          }
        />
        <Route
          path="/models/core"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <CoreModelDesktop />
            </ProtectedRoute>
          }
        />
        <Route
          path="/models/fine-tuned"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <FineTunedModelDesktop />
            </ProtectedRoute>
          }
        />
        <Route
          path="/models/training-data"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <TrainingDataDesktop />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ondemand"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <OnDemandConsole />
            </ProtectedRoute>
          }
        />
        <Route
          path="/personas"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <PersonaDesktop />
            </ProtectedRoute>
          }
        />
        <Route
          path="/presentation/:id"
          element={
            <AuthOnlyRoute>
              <PresentationDisplay />
            </AuthOnlyRoute>
          }
        />
        <Route
          path="/presentations"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <PresentationDesktop />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <User>{(profile) => <UserProfile profile={profile} />}</User>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/outreach"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <OutreachMonitor />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/outreach"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <OutreachMonitor />
            </ProtectedRoute>
          }
        />

        <Route
          path="/sessions"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <SessionConsole />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fragments"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <FragmentsConsole />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <SettingsConsole />
            </ProtectedRoute>
          }
        />
        <Route
          path="/simulants"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <SimulantDesktop />
            </ProtectedRoute>
          }
        />
        <Route
          path="/skills"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <SkillDesktop />
            </ProtectedRoute>
          }
        />
        <Route
          path="/topics"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <TopicDesktop />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/monitor"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <UserAnalysisDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/custom_pronunciations"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <CustomPronunciationConsole />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotImplemented />} />
        <Route
          path="/twilio_sender"
          element={
            <ProtectedRoute openAboutModal={openAboutModal}>
              <TwilioSenderDesktop />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <UnprotectedRoute>
              <ComplianceOnboarding />
            </UnprotectedRoute>
          }
        />
      </Routes>
      <HelpModal isOpen={isOpen} onClose={closeHelpModal} fileName={fileName} />
      <AboutModal isOpen={isAboutModalOpen} onClose={closeAboutModal} />
      <ConfirmationDialog />
    </MsalProvider>
  );
}

function App() {
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [warningDate, setWarningDate] = useState(null);
  const { buildVersion, buildWarningVersion } = usePollForBuildId();

  useEffect(() => {
    const hostname = window.location.hostname;
    if (hostname === "ai.assistant.lumeris.com") {
      window.location.href = window.location.href.replace(
        "ai.assistant.lumeris.com",
        "ai.agent.lumeris.com",
      );
      return;
    }
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      document.title = "AI Agent - Local";
    } else if (hostname.indexOf(".dev.") !== -1) {
      document.title = "AI Agent - DEV";
    } else if (hostname.indexOf(".qae.") !== -1) {
      document.title = "AI Agent - QAE";
    } else if (hostname.indexOf(".sit.") !== -1) {
      document.title = "AI Agent - SIT";
    } else if (hostname.indexOf(".uat.") !== -1) {
      document.title = "AI Agent - UAT";
    } else {
      document.title = "AI Agent";
    }

    if (!isChrome()) {
      setWarningMessage(
        "For the best experience, please use Google Chrome. Other browsers are not fully supported.",
      );
      setShowWarning(true);
    }
  }, []);

  useEffect(() => {
    if (buildWarningVersion && buildVersion) {
      if (buildVersion !== buildWarningVersion) {
        setShowWarning(false);
        setWarningDate(null);
      }
      if (buildVersion === buildWarningVersion) {
        setWarningMessage(
          "Please save now. System maintenance about to begin. Service could be unavailable for 15 minutes.",
        );
        setWarningDate(new Date());
        setShowWarning(true);
      }
    }
  }, [buildVersion, buildWarningVersion]);

  return (
    <Router>
      {showWarning && (
        <BrowserWarning message={warningMessage} warningDate={warningDate} />
      )}
      <ToastProvider>
        <LoadingProvider>
          <HelpModalProvider>
            <PinsProvider>
              <CardSelectorProvider>
                <ConfirmationProvider>
                  <AppRoutes />
                  <LoadingControl />
                </ConfirmationProvider>
              </CardSelectorProvider>
            </PinsProvider>
          </HelpModalProvider>
        </LoadingProvider>
      </ToastProvider>
    </Router>
  );
}

export default App;
